'use client';

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Check, Search, X, MapPin } from 'lucide-react';
import {
  VENUE_COUNTRIES,
  statesForCountry,
  citiesForState,
} from '@/components/event-wizard/venue-location-data';

export interface LocationSelectorProps {
  country: string;
  state: string;
  city: string;
  onChange: (patch: { country?: string; state?: string; city?: string }) => void;
  required?: boolean;
  countryLabel?: string;
  stateLabel?: string;
  cityLabel?: string;
  className?: string;
}

export function LocationSelector({
  country,
  state,
  city,
  onChange,
  required = true,
  countryLabel = 'Country',
  stateLabel = 'State',
  cityLabel = 'City',
  className = '',
}: LocationSelectorProps) {
  // Dropdown open states
  const [countryOpen, setCountryOpen] = useState(false);
  const [stateOpen, setStateOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);

  // Search filter queries
  const [countryQuery, setCountryQuery] = useState('');
  const [stateQuery, setStateQuery] = useState('');
  const [cityQuery, setCityQuery] = useState('');

  // DOM Refs
  const countryRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef<HTMLDivElement>(null);
  const cityRef = useRef<HTMLDivElement>(null);

  // Handle outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (countryRef.current && !countryRef.current.contains(e.target as Node)) {
        setCountryOpen(false);
      }
      if (stateRef.current && !stateRef.current.contains(e.target as Node)) {
        setStateOpen(false);
      }
      if (cityRef.current && !cityRef.current.contains(e.target as Node)) {
        setCityOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filtered countries
  const filteredCountries = useMemo(() => {
    const q = countryQuery.trim().toLowerCase();
    if (!q) return VENUE_COUNTRIES;
    return VENUE_COUNTRIES.filter((c) => c.name.toLowerCase().includes(q));
  }, [countryQuery]);

  // States available for selected country
  const availableStates = useMemo(() => {
    if (!country.trim()) return [];
    return statesForCountry(country);
  }, [country]);

  // Filtered states
  const filteredStates = useMemo(() => {
    const q = stateQuery.trim().toLowerCase();
    if (!q) return availableStates;
    return availableStates.filter((s) => s.name.toLowerCase().includes(q));
  }, [availableStates, stateQuery]);

  // City suggestions based on country and state
  const citySuggestions = useMemo(() => {
    const trimmedCountry = country.trim();
    const trimmedState = state.trim();
    let candidates: string[] = [];

    if (trimmedCountry && trimmedState) {
      candidates = citiesForState(trimmedCountry, trimmedState);
    } else if (trimmedCountry) {
      const allStates = statesForCountry(trimmedCountry);
      const set = new Set<string>();
      allStates.forEach((s) => s.cities.forEach((c) => set.add(c)));
      candidates = Array.from(set).sort();
    }

    const q = cityQuery.trim().toLowerCase() || city.trim().toLowerCase();
    if (!q) return candidates.slice(0, 40);
    return candidates.filter((c) => c.toLowerCase().includes(q)).slice(0, 40);
  }, [country, state, city, cityQuery]);

  const buttonBaseClass =
    'w-full flex items-center justify-between rounded-xl bg-[#F9F7F0] border border-[#5F6F52]/40 px-4 py-3 text-sm text-[#2D3536] hover:border-[#5F6F52] focus:outline-none focus:ring-2 focus:ring-[#5F6F52]/20 transition-all text-left';

  return (
    <div className={`grid gap-6 md:grid-cols-2 ${className}`}>
      {/* Country Select */}
      <div className="w-full">
        <label className="block text-sm font-semibold text-[#111827] mb-2">
          {countryLabel}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        <div ref={countryRef} className="relative">
          <button
            type="button"
            onClick={() => {
              setCountryOpen((prev) => !prev);
              setCountryQuery('');
            }}
            className={`${buttonBaseClass} ${countryOpen ? 'ring-2 ring-[#5F6F52]/20 border-[#5F6F52]' : ''}`}
            aria-haspopup="listbox"
            aria-expanded={countryOpen}
          >
            <span className={country ? 'text-[#2D3536] font-medium' : 'text-[#9CA3AF]'}>
              {country || 'Select or search country'}
            </span>
            <div className="flex items-center gap-1.5 shrink-0">
              {country && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange({ country: '', state: '', city: '' });
                    setCountryQuery('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation();
                      e.preventDefault();
                      onChange({ country: '', state: '', city: '' });
                      setCountryQuery('');
                    }
                  }}
                  className="p-0.5 text-gray-400 hover:text-gray-600 rounded-full cursor-pointer inline-flex items-center justify-center"
                  title="Clear country"
                >
                  <X className="h-3.5 w-3.5" />
                </span>
              )}
              <ChevronDown className="h-4 w-4 text-[#5F6F52] opacity-80" />
            </div>
          </button>

          {countryOpen && (
            <div className="absolute z-50 mt-1 max-h-60 w-full overflow-hidden rounded-xl border border-[#E5E2D9] bg-white shadow-2xl animate-in fade-in duration-150">
              <div className="flex items-center border-b border-[#E5E2D9] px-3 py-2 bg-[#F9F7F0]/60">
                <Search className="h-4 w-4 text-[#5F6F52]/70 shrink-0" />
                <input
                  type="text"
                  value={countryQuery}
                  onChange={(e) => setCountryQuery(e.target.value)}
                  placeholder="Type to search country…"
                  className="w-full bg-transparent px-2 text-sm text-[#2D3536] placeholder-[#9CA3AF] focus:outline-none"
                  autoFocus
                />
                {countryQuery && (
                  <button
                    type="button"
                    onClick={() => setCountryQuery('')}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              <div className="max-h-48 overflow-y-auto p-1 text-sm">
                {filteredCountries.map((c) => {
                  const isSelected = country.toLowerCase() === c.name.toLowerCase();
                  return (
                    <button
                      key={c.name}
                      type="button"
                      onClick={() => {
                        onChange({ country: c.name, state: '', city: '' });
                        setCountryOpen(false);
                        setCountryQuery('');
                      }}
                      className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left hover:bg-[#F9F7F0] transition-colors ${
                        isSelected ? 'font-semibold text-[#5F6F52] bg-[#F9F7F0]/80' : 'text-[#2D3536]'
                      }`}
                    >
                      <span>{c.name}</span>
                      {isSelected && <Check className="h-4 w-4 text-[#5F6F52]" />}
                    </button>
                  );
                })}

                {filteredCountries.length === 0 && (
                  <div className="p-3 text-center text-xs text-[#2D3536]/70">
                    <p>No predefined country found for &ldquo;{countryQuery}&rdquo;.</p>
                    <button
                      type="button"
                      onClick={() => {
                        onChange({ country: countryQuery.trim(), state: '', city: '' });
                        setCountryOpen(false);
                        setCountryQuery('');
                      }}
                      className="mt-2 text-xs font-semibold text-[#5F6F52] underline"
                    >
                      Use &ldquo;{countryQuery.trim()}&rdquo; as custom country
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* State Select */}
      <div className="w-full">
        <label className="block text-sm font-semibold text-[#111827] mb-2">
          {stateLabel}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        <div ref={stateRef} className="relative">
          <button
            type="button"
            disabled={!country.trim()}
            onClick={() => {
              if (!country.trim()) return;
              setStateOpen((prev) => !prev);
              setStateQuery('');
            }}
            className={`${buttonBaseClass} ${
              !country.trim() ? 'opacity-60 cursor-not-allowed bg-slate-50' : ''
            } ${stateOpen ? 'ring-2 ring-[#5F6F52]/20 border-[#5F6F52]' : ''}`}
            aria-haspopup="listbox"
            aria-expanded={stateOpen}
          >
            <span className={state ? 'text-[#2D3536] font-medium' : 'text-[#9CA3AF]'}>
              {state || (country.trim() ? 'Select or search state / region' : 'Select country first')}
            </span>
            <div className="flex items-center gap-1.5 shrink-0">
              {state && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange({ state: '', city: '' });
                    setStateQuery('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation();
                      e.preventDefault();
                      onChange({ state: '', city: '' });
                      setStateQuery('');
                    }
                  }}
                  className="p-0.5 text-gray-400 hover:text-gray-600 rounded-full cursor-pointer inline-flex items-center justify-center"
                  title="Clear state"
                >
                  <X className="h-3.5 w-3.5" />
                </span>
              )}
              <ChevronDown className="h-4 w-4 text-[#5F6F52] opacity-80" />
            </div>
          </button>

          {stateOpen && country.trim() && (
            <div className="absolute z-50 mt-1 max-h-60 w-full overflow-hidden rounded-xl border border-[#E5E2D9] bg-white shadow-2xl animate-in fade-in duration-150">
              <div className="flex items-center border-b border-[#E5E2D9] px-3 py-2 bg-[#F9F7F0]/60">
                <Search className="h-4 w-4 text-[#5F6F52]/70 shrink-0" />
                <input
                  type="text"
                  value={stateQuery}
                  onChange={(e) => setStateQuery(e.target.value)}
                  placeholder="Type to search state / region…"
                  className="w-full bg-transparent px-2 text-sm text-[#2D3536] placeholder-[#9CA3AF] focus:outline-none"
                  autoFocus
                />
                {stateQuery && (
                  <button
                    type="button"
                    onClick={() => setStateQuery('')}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              <div className="max-h-48 overflow-y-auto p-1 text-sm">
                {filteredStates.map((s) => {
                  const isSelected = state.toLowerCase() === s.name.toLowerCase();
                  return (
                    <button
                      key={s.name}
                      type="button"
                      onClick={() => {
                        onChange({ state: s.name, city: '' });
                        setStateOpen(false);
                        setStateQuery('');
                      }}
                      className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left hover:bg-[#F9F7F0] transition-colors ${
                        isSelected ? 'font-semibold text-[#5F6F52] bg-[#F9F7F0]/80' : 'text-[#2D3536]'
                      }`}
                    >
                      <span>{s.name}</span>
                      {isSelected && <Check className="h-4 w-4 text-[#5F6F52]" />}
                    </button>
                  );
                })}

                {filteredStates.length === 0 && (
                  <div className="p-3 text-center text-xs text-[#2D3536]/70">
                    <p>
                      {availableStates.length === 0
                        ? `No predefined states for ${country}.`
                        : `No state matches "${stateQuery}".`}
                    </p>
                    {stateQuery.trim() ? (
                      <button
                        type="button"
                        onClick={() => {
                          onChange({ state: stateQuery.trim(), city: '' });
                          setStateOpen(false);
                          setStateQuery('');
                        }}
                        className="mt-2 text-xs font-semibold text-[#5F6F52] underline"
                      >
                        Use &ldquo;{stateQuery.trim()}&rdquo; as custom state
                      </button>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="w-full md:col-span-2">
        <label className="block text-sm font-semibold text-[#111827] mb-2">
          {cityLabel}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        <div ref={cityRef} className="relative">
          <button
            type="button"
            disabled={!country.trim()}
            onClick={() => {
              if (!country.trim()) return;
              setCityOpen((prev) => !prev);
              setCityQuery('');
            }}
            className={`${buttonBaseClass} ${
              !country.trim() ? 'opacity-60 cursor-not-allowed bg-slate-50' : ''
            } ${cityOpen ? 'ring-2 ring-[#5F6F52]/20 border-[#5F6F52]' : ''}`}
            aria-haspopup="listbox"
            aria-expanded={cityOpen}
          >
            <span className={city ? 'text-[#2D3536] font-medium' : 'text-[#9CA3AF]'}>
              {city || (country.trim() ? 'Select or search city' : 'Select country first')}
            </span>
            <div className="flex items-center gap-1.5 shrink-0">
              {city && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange({ city: '' });
                    setCityQuery('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation();
                      e.preventDefault();
                      onChange({ city: '' });
                      setCityQuery('');
                    }
                  }}
                  className="p-0.5 text-gray-400 hover:text-gray-600 rounded-full cursor-pointer inline-flex items-center justify-center"
                  title="Clear city"
                >
                  <X className="h-3.5 w-3.5" />
                </span>
              )}
              <ChevronDown className="h-4 w-4 text-[#5F6F52] opacity-80" />
            </div>
          </button>

          {cityOpen && country.trim() && (
            <div className="absolute z-40 mt-1 max-h-60 w-full overflow-hidden rounded-xl border border-[#E5E2D9] bg-white shadow-2xl animate-in fade-in duration-150">
              <div className="flex items-center border-b border-[#E5E2D9] px-3 py-2 bg-[#F9F7F0]/60">
                <Search className="h-4 w-4 text-[#5F6F52]/70 shrink-0" />
                <input
                  type="text"
                  value={cityQuery}
                  onChange={(e) => setCityQuery(e.target.value)}
                  placeholder="Type to search city…"
                  className="w-full bg-transparent px-2 text-sm text-[#2D3536] placeholder-[#9CA3AF] focus:outline-none"
                  autoFocus
                />
              </div>
              <div className="max-h-48 overflow-y-auto p-1 text-sm">
                {citySuggestions.map((suggestedCity) => {
                  const isSelected = city.toLowerCase() === suggestedCity.toLowerCase();
                  return (
                    <button
                      key={suggestedCity}
                      type="button"
                      onClick={() => {
                        onChange({ city: suggestedCity });
                        setCityOpen(false);
                        setCityQuery('');
                      }}
                      className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left hover:bg-[#F9F7F0] transition-colors ${
                        isSelected ? 'font-semibold text-[#5F6F52] bg-[#F9F7F0]/80' : 'text-[#2D3536]'
                      }`}
                    >
                      <span>{suggestedCity}</span>
                      {isSelected && <Check className="h-4 w-4 text-[#5F6F52]" />}
                    </button>
                  );
                })}
                {citySuggestions.length === 0 && cityQuery.trim() ? (
                  <div className="p-3 text-center text-xs text-[#2D3536]/70">
                    <button
                      type="button"
                      onClick={() => {
                        onChange({ city: cityQuery.trim() });
                        setCityOpen(false);
                        setCityQuery('');
                      }}
                      className="text-xs font-semibold text-[#5F6F52] underline"
                    >
                      Use &ldquo;{cityQuery.trim()}&rdquo; as custom city
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
